import React, { useEffect } from 'react';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import {
  EntityRefLink,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import { Entity, RELATION_HAS_PART } from '@backstage/catalog-model';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { Link as RouterLink } from 'react-router-dom';

const entityKind = (entity: Entity) =>
  entity.kind?.toLocaleLowerCase('en-US') ?? '';

const apiHasSwaggerDefinition = (entity: Entity): boolean => {
  if (entityKind(entity) !== 'api') {
    return false;
  }

  const spec = entity.spec as Record<string, unknown> | undefined;
  const definition = spec?.definition;

  if (definition === undefined || definition === null) {
    return false;
  }

  if (typeof definition === 'string') {
    return definition.trim().length > 0;
  }

  if (typeof definition === 'object') {
    return Object.keys(definition as Record<string, unknown>).length > 0;
  }

  return false;
};

const ComponentMissingDocsAlert = () => {
  const { entity } = useEntity();

  if (entityKind(entity) !== 'component' || isTechDocsAvailable(entity)) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <Alert severity="warning">
        <AlertTitle>Missing TechDocs</AlertTitle>
        <Typography variant="body2">
          This component has no TechDocs. Add a <code>backstage.io/techdocs-ref</code> annotation in the catalog metadata.
        </Typography>
        <Box mt={1}>
          <Button
            component={RouterLink}
            to="manifest"
            variant="outlined"
            size="small"
            color="primary"
            style={{ marginRight: 8 }}
          >
            Open manifest editor
          </Button>
        </Box>
      </Alert>
    </Grid>
  );
};

const ApiMissingSwaggerAlert = () => {
  const { entity } = useEntity();

  if (entityKind(entity) !== 'api' || apiHasSwaggerDefinition(entity)) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <Alert severity="warning">
        <AlertTitle>Missing Swagger definition</AlertTitle>
        <Typography variant="body2">
          This API is missing an OpenAPI/Swagger definition. Provide an <code>openapi</code> spec via <code>spec.definition</code> to enable API docs.
        </Typography>
        <Box mt={1}>
          <Button
            component={RouterLink}
            to="manifest"
            variant="outlined"
            size="small"
            color="primary"
            style={{ marginRight: 8 }}
          >
            Open manifest editor
          </Button>
        </Box>
      </Alert>
    </Grid>
  );
};

const MissingDocumentationList = ({ items }: { items: Entity[] }) => (
  <Box component="ul" pl={3} my={0}>
    {items.map(child => (
      <Box
        key={
          child.metadata.uid ??
          `${child.kind}:${child.metadata.namespace ?? 'default'}/${child.metadata.name}`
        }
        component="li"
      >
        <EntityRefLink entityRef={child} />
      </Box>
    ))}
  </Box>
);

const SystemMissingDocsBanner = ({ systemEntity }: { systemEntity: Entity }) => {
  const {
    entities: componentEntities,
    loading: componentsLoading,
    error: componentsError,
  } = useRelatedEntities(systemEntity, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });
  const {
    entities: apiEntities,
    loading: apisLoading,
    error: apisError,
  } = useRelatedEntities(systemEntity, {
    type: RELATION_HAS_PART,
    kind: 'API',
  });

  useEffect(() => {
    if (componentsError) {
      console.error(
        'Failed to load related components for documentation banner',
        componentsError,
      );
    }
  }, [componentsError]);

  useEffect(() => {
    if (apisError) {
      console.error(
        'Failed to load related APIs for documentation banner',
        apisError,
      );
    }
  }, [apisError]);

  if (componentsLoading || apisLoading) {
    return null;
  }

  const missingComponentDocs =
    (componentEntities ?? []).filter(child => !isTechDocsAvailable(child));
  const missingApiDocs =
    (apiEntities ?? []).filter(child => !apiHasSwaggerDefinition(child));

  if (missingComponentDocs.length === 0 && missingApiDocs.length === 0) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <Alert severity="warning">
        <AlertTitle>Documentation gaps in this system</AlertTitle>
        {missingComponentDocs.length > 0 && (
          <>
            <Typography variant="body2">
              The following components are missing TechDocs:
            </Typography>
            <MissingDocumentationList items={missingComponentDocs} />
          </>
        )}
        {missingApiDocs.length > 0 && (
          <>
            <Typography variant="body2">
              The following APIs are missing Swagger definitions:
            </Typography>
            <MissingDocumentationList items={missingApiDocs} />
          </>
        )}
      </Alert>
    </Grid>
  );
};

const SystemMissingDocsAlert = () => {
  const { entity } = useEntity();

  if (entityKind(entity) !== 'system') {
    return null;
  }

  return <SystemMissingDocsBanner systemEntity={entity} />;
};

export const DocumentationAlerts = () => (
  <>
    <ComponentMissingDocsAlert />
    <ApiMissingSwaggerAlert />
    <SystemMissingDocsAlert />
  </>
);
